// Minimal DXF writer. Emits the same limited set of entity types the parser
// consumes (LINE, ARC, CIRCLE, LWPOLYLINE, POLYLINE) so a round-trip through
// the editor doesn't destroy geometry.

import type { Entity, Layer } from "../state/types.js";

export interface ExportInput {
  entities: Entity[];
  layers: Record<string, Layer>;
  unitCode: number;
  // Multiplier applied to every emitted coordinate. Callers set this to
  // convert from the in-memory unit (dxfUnit) to the chosen output unit
  // when they differ; pass 1 to emit coords as-is.
  scale?: number;
  // Optional $CUSTOMPROPERTY pairs to write into HEADER. Round-trips
  // DXF_HEALED (and any other tags picked up on load) so a downloaded and
  // re-uploaded file is still recognised as healed, and lets callers stamp
  // extra tags like DXF_UNIT_OVERRIDDEN when the user changes units.
  customVars?: Record<string, string>;
}

export function exportDxf({ entities, unitCode, scale, customVars }: ExportInput): string {
  const chunks: string[] = [];
  const s = scale ?? 1;

  const emit = (code: number, value: string | number): void => {
    chunks.push(String(code));
    chunks.push(String(value));
  };
  // Scale coordinate values, leave everything else alone. Coord group codes
  // in DXF are 10/11/12/13 for X, 20/21/22/23 for Y, 30/31/32/33 for Z,
  // and 40 is a length (radius, thickness). Angles (50/51) and flags stay
  // untouched.
  const emitCoord = (code: number, value: number): void => {
    emit(code, value * s);
  };

  emit(0, "SECTION");
  emit(2, "HEADER");
  // ezdxf's strict loader (used by heal_server /convert-to-glb) rejects
  // LWPOLYLINE without the `AcDbPolyline` subclass marker and derives entity
  // format from $ACADVER. AC1015 (R2000) is the first version with modern
  // subclass markers + LWPOLYLINE, so declare it explicitly.
  emit(9, "$ACADVER");
  emit(1, "AC1015");
  emit(9, "$INSUNITS");
  emit(70, unitCode || 4);
  if (customVars) {
    // Each $CUSTOMPROPERTY is a PAIR: TAG line names the variable, then
    // $CUSTOMPROPERTY carries the value. Both use group code 1.
    for (const [tag, value] of Object.entries(customVars)) {
      emit(9, "$CUSTOMPROPERTYTAG");
      emit(1, tag);
      emit(9, "$CUSTOMPROPERTY");
      emit(1, value);
    }
  }
  emit(0, "ENDSEC");
  emit(0, "SECTION");
  emit(2, "ENTITIES");

  for (const entity of entities) {
    const d = entity.data;
    const layerName = entity.layer || "0";
    // Polylines get emitted as LWPOLYLINE regardless of input type — the
    // exporter's vertex-array format is the LWPOLYLINE wire format (inline
    // vertices, group 90 count), not old-style POLYLINE with VERTEX/SEQEND
    // sub-entities. Downstream tooling (fix_dxf) also normalises to
    // LWPOLYLINE anyway.
    const outType = d.type === "POLYLINE" ? "LWPOLYLINE" : d.type;
    emit(0, outType);
    emit(100, "AcDbEntity");
    emit(8, layerName);

    switch (d.type) {
      case "LINE":
        emit(100, "AcDbLine");
        emitCoord(10, d.startX ?? 0);
        emitCoord(20, d.startY ?? 0);
        emitCoord(11, d.endX ?? 100);
        emitCoord(21, d.endY ?? 100);
        break;
      case "CIRCLE":
        emit(100, "AcDbCircle");
        emitCoord(10, d.startX ?? d.centerX ?? 0);
        emitCoord(20, d.startY ?? d.centerY ?? 0);
        emitCoord(40, d.radius ?? 10);
        break;
      case "ARC":
        emit(100, "AcDbCircle");
        emitCoord(10, d.startX ?? d.centerX ?? 0);
        emitCoord(20, d.startY ?? d.centerY ?? 0);
        emitCoord(40, d.radius ?? 10);
        emit(100, "AcDbArc");
        emit(50, d.startAngle ?? 0);
        emit(51, d.endAngle ?? 90);
        break;
      case "LWPOLYLINE":
      case "POLYLINE":
        if (d.vertices && d.vertices.length > 0) {
          emit(100, "AcDbPolyline");
          emit(90, d.vertices.length);
          emit(70, d.flags ?? 1);
          for (const v of d.vertices) {
            emitCoord(10, v.x ?? 0);
            emitCoord(20, v.y ?? 0);
            // Preserve arc-encoding bulge on round-trip. Bulge is a ratio,
            // not a coordinate, so it isn't multiplied by `scale`.
            if (v.b !== undefined && v.b !== 0) emit(42, v.b);
          }
        }
        break;
    }
  }

  emit(0, "ENDSEC");
  emit(0, "EOF");

  return chunks.join("\n");
}
